import React, { useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { Sheet } from '@pkg/components';
import { GroupFilters, GroupSorting } from '@pkg/components/Sections/Groups';
import { useFilteredItems, useGroupId } from '@pkg/hooks';
import { convertNftFiltersToServerQuery, useMediaQuery, useNftFilter } from '@pkg/providers';

import { GroupFiltersWrapper } from '@app/pages/Groups/Group/styles';
import { FiltersListToggle } from '@app/pages/Prototype/NftManagerPanel/components/FiltersListToggle';
import { FiltersListToggleWrapper } from '@app/pages/Prototype/NftManagerPanel/components/SidePanelContent/styles';

import { NftList } from '../../components';

export interface NftCollectionProps {
    podName?: string;
    nftCount?: number;
}

export function NftCollection({ podName, nftCount }: NftCollectionProps) {
    const { tablet } = useMediaQuery();
    const { t: translate } = useTranslation('groups');

    const [filters] = useNftFilter();
    // We might want to memoize this if we find that
    // `useFilteredItems()` is re-running unnecessarily.
    const convertedFilters = convertNftFiltersToServerQuery(filters);

    const groupId = useGroupId();
    const { items, isLoading, isError, hasNextPage, isFetching, isPreviousData, fetchNextPage } = useFilteredItems({
        groupId,
        filters: convertedFilters,
    });

    // TODO: Replace this hook with a call to `filters.total`.
    // Currently, that property will not filter out "default values",
    // so that will have to be done manually.
    // const filtersCount = useGroupFilterCount();

    const [showSidebar, setShowSidebar] = useState(true);

    function handleSidebarToggle() {
        setShowSidebar((show) => !show);
    }

    const hasControls = tablet;

    const filtersToggleMarkup = hasControls ? (
        <AnimatePresence initial={false}>
            <Sheet.Layout.Columns.Secondary>
                <FiltersListToggleWrapper>
                    {/* TODO: Removed `count={filtersCount}` until we can update the `useGroupFilterCount()` hook. */}
                    <FiltersListToggle open={showSidebar} onToggle={handleSidebarToggle} />
                </FiltersListToggleWrapper>
            </Sheet.Layout.Columns.Secondary>
        </AnimatePresence>
    ) : null;

    const controlsMarkup = hasControls ? (
        <Sheet.Layout.Section padded>
            <Sheet.Layout.Columns stretch>
                <Sheet.Layout.Columns.Primary>
                    <GroupSorting />
                </Sheet.Layout.Columns.Primary>

                {filtersToggleMarkup}
            </Sheet.Layout.Columns>
        </Sheet.Layout.Section>
    ) : null;

    const sidebarMarkup =
        tablet && showSidebar ? (
            <Sheet.Layout.Columns.Secondary key="GroupImagePicker-Columns-Secondary" padded scrollable>
                <GroupFiltersWrapper>
                    <GroupFilters />
                </GroupFiltersWrapper>
            </Sheet.Layout.Columns.Secondary>
        ) : null;

    // TODO: Let's fix the data response to avoid this.
    const pluckedItems = items.map(({ item }) => item);

    return (
        <Sheet.Layout>
            <Sheet.Layout.Header
                title={translate('pods_image_results_title', { name: podName })}
                subtitle={translate('pods_image_results_subtitle')}
                metaText={nftCount !== undefined ? `(${nftCount})` : undefined}
            />

            {controlsMarkup}

            <Sheet.Layout.Columns stretch>
                <Sheet.Layout.Columns.Primary padded scrollable>
                    <NftList
                        items={pluckedItems}
                        loading={isLoading}
                        hasError={isError}
                        hasMoreResults={hasNextPage}
                        fetchingResults={isFetching}
                        staleResults={isPreviousData}
                        onLoadMore={fetchNextPage}
                    />
                </Sheet.Layout.Columns.Primary>

                <AnimatePresence initial={false}>{sidebarMarkup}</AnimatePresence>
            </Sheet.Layout.Columns>
        </Sheet.Layout>
    );
}
